import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import {
  arrange,
  choicesOf,
  isTableFiltered,
  nextTableSort,
  NO_TABLE_FILTER,
  textOf,
  type TableFilter,
  type TableSort,
} from './tableControls'
import { SortButton, TableToolbar, ToolbarSelect } from './TableToolbar'

type El = ReactElement<{ children?: ReactNode; colSpan?: number; className?: string }>

const elements = (node: ReactNode): El[] =>
  Children.toArray(node).filter((c): c is El => isValidElement(c))

/** A row's cells' texts; a group of rows (a Fragment) is read by its first row. */
function cellsOf(row: El, columns: number): string[] {
  const tr = row.type === Fragment ? elements(row.props.children).find((r) => r.type === 'tr') : row
  if (tr?.type === 'tr') return elements(tr.props.children).map((td) => textOf(td.props.children))
  return Array.from({ length: columns }, () => textOf(row))
}

/*
  A table of a handful of rows is read at a glance: its headings still sort, but a search box
  and dropdowns over three pastures would be clutter (2026-10-27).
*/
const TOOLBAR_FROM_ROWS = 6

/**
 * The table TableWrap holds, with its headings as sort buttons and, over a longer table, a
 * toolbar to search it and pick values from its repeating columns (tableControls.ts). `scroll`
 * wraps the table alone in TableWrap's scroller, so the toolbar stays put when a wide table
 * scrolls sideways. A table it cannot read plainly (no head or body, a heading that spans
 * columns, fewer than two rows) is shown as it is.
 */
export function ArrangedTable({
  table,
  scroll,
}: {
  table: ReactElement<{ children?: ReactNode }>
  scroll: (table: ReactNode) => ReactElement
}) {
  const [sort, setSort] = useState<TableSort | null>(null)
  const [filter, setFilter] = useState<TableFilter>(NO_TABLE_FILTER)

  const parts = elements(table.props.children)
  const head = parts.find((p) => p.type === 'thead')
  const body = parts.find((p) => p.type === 'tbody')
  const headRows = head ? elements(head.props.children) : []
  const headRow = headRows.length === 1 && headRows[0]!.type === 'tr' ? headRows[0]! : null
  const ths = headRow ? elements(headRow.props.children) : []
  const rows = body ? elements(body.props.children) : []
  const filtered = isTableFiltered(filter)
  if (!head || !body || !headRow || ths.some((th) => (th.props.colSpan ?? 1) > 1)) {
    return scroll(table)
  }
  if (rows.length < 2 && !filtered) return scroll(table)

  const names = ths.map((th) => textOf(th.props.children).trim())
  const cells = rows.map((r) => cellsOf(r, ths.length))
  const order = arrange(cells, filter, sort)
  const clear = () => setFilter(NO_TABLE_FILTER)

  const header = cloneElement(
    headRow,
    {},
    ths.map((th, c) => {
      if (!names[c]) return th
      const state = sort?.column !== c ? 'none' : sort.descending ? 'descending' : 'ascending'
      return cloneElement(
        th as ReactElement<{ 'aria-sort'?: string }>,
        { key: th.key ?? c, 'aria-sort': state },
        <SortButton sort={state} onClick={() => setSort(nextTableSort(sort, c))}>
          {th.props.children}
        </SortButton>,
      )
    }),
  )

  const arranged = cloneElement(
    table,
    {},
    parts.map((p) =>
      p === head
        ? cloneElement(head, { key: p.key ?? 'head' }, header)
        : p === body
          ? cloneElement(
              body,
              { key: p.key ?? 'body' },
              order.length
                ? order.map((i) => rows[i])
                : [
                    <tr key="none">
                      <td colSpan={ths.length} className="text-muted">
                        No rows match.{' '}
                        <button type="button" className="linkButton" onClick={clear}>
                          Clear filters
                        </button>
                      </td>
                    </tr>,
                  ],
            )
          : p,
    ),
  )

  if (rows.length < TOOLBAR_FROM_ROWS) return scroll(arranged)

  const pickable = names
    .map((name, c) => ({ name, c, values: name ? choicesOf(cells, c) : null }))
    .filter((p): p is { name: string; c: number; values: string[] } => p.values !== null)

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <TableToolbar
        search={filter.search}
        onSearch={(search) => setFilter({ ...filter, search })}
        shown={order.length}
        total={rows.length}
        filtered={filtered}
        onClear={clear}
      >
        {pickable.map(({ name, c, values }) => (
          <ToolbarSelect
            key={c}
            label={name}
            value={filter.picks[c] ?? ''}
            values={values}
            onChange={(v) => {
              const picks = [...filter.picks]
              picks[c] = v
              setFilter({ ...filter, picks })
            }}
          />
        ))}
      </TableToolbar>
      {scroll(arranged)}
    </div>
  )
}
